import React, { useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useNavigate, useParams } from 'react-router-dom';
import type { Location } from 'wasp/entities';
import { useLocationContext, LocationWithHierarchy } from '../../contexts/LocationContext';
import { findLocationBySlug } from '../../lib/locationPaths';
import { sortHubLocations } from '../../../shared/hubLocations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

interface LocationDropdownProps {
  className?: string;
}

/** Hub-style locations users can switch between (excludes Canada / taxonomy-only nodes). */
function getSelectableLocations(
  locations: LocationWithHierarchy[],
  selectedId: string | undefined
): LocationWithHierarchy[] {
  return sortHubLocations(
    locations.filter(
      (loc) => loc.slug && loc.locationCode !== 'CA' && loc.id !== selectedId
    )
  );
}

export default function LocationDropdown({ className }: LocationDropdownProps) {
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();

  const {
    selectedLocation,
    setSelectedLocation,
    locations,
    isLoading,
  } = useLocationContext();

  const typedLocations = (locations ?? []) as LocationWithHierarchy[];

  const selectableLocations = useMemo(
    () => getSelectableLocations(typedLocations, selectedLocation?.id),
    [typedLocations, selectedLocation?.id]
  );

  useEffect(() => {
    if (typedLocations.length && !selectedLocation) {
      if (params.slug) {
        const locationBySlug = findLocationBySlug(typedLocations, params.slug);
        setSelectedLocation(locationBySlug ?? typedLocations[0]);
      } else {
        const firstHub =
          typedLocations.find((l) => l.slug && l.locationCode !== 'CA') ?? typedLocations[0];
        setSelectedLocation(firstHub);
      }
    }
  }, [locations, params.slug, selectedLocation, setSelectedLocation]);

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location as LocationWithHierarchy);

    const currentPath = window.location.pathname;
    const locIdentifier =
      location.slug ||
      (location.locationCode ? location.locationCode.toLowerCase() : null);

    if (!locIdentifier) {
      if (!currentPath.includes('/admin/')) navigate('/dashboard');
      return;
    }

    if (
      currentPath.includes('/admin/') ||
      currentPath === '/dashboard' ||
      currentPath === '/reception'
    ) {
      return;
    }

    const segments = currentPath.split('/').filter(Boolean);
    const section =
      segments.length >= 2 ? segments[1] : 'information-desk';

    if (currentPath.includes('/notices/add')) {
      navigate(`/${locIdentifier}/notices/add`);
    } else if (currentPath.includes('/notices/') && currentPath.includes('/edit')) {
      const noticeId = currentPath.split('/notices/')[1]?.split('/edit')[0];
      navigate(noticeId ? `/${locIdentifier}/notices/${noticeId}/edit` : `/${locIdentifier}/notices`);
    } else if (
      currentPath.includes('/notices/') &&
      !currentPath.includes('/add') &&
      !currentPath.includes('/edit')
    ) {
      const noticeId = currentPath.split('/notices/')[1];
      navigate(noticeId ? `/${locIdentifier}/notices/${noticeId}` : `/${locIdentifier}/notices`);
    } else if (currentPath.includes('/notices')) {
      navigate(`/${locIdentifier}/notices`);
    } else if (currentPath.includes('/tenants')) {
      navigate(`/${locIdentifier}/tenants`);
    } else if (currentPath.includes('/events')) {
      navigate(`/${locIdentifier}/events`);
    } else if (currentPath.includes('/facilities')) {
      navigate(`/${locIdentifier}/facilities`);
    } else if (currentPath.includes('/buy-sell')) {
      navigate(`/${locIdentifier}/buy-sell`);
    } else if (currentPath.includes('/food-court')) {
      navigate(`/${locIdentifier}/food-court`);
    } else if (currentPath.includes('/lost-found')) {
      navigate(`/${locIdentifier}/lost-found`);
    } else if (currentPath.includes('/services')) {
      navigate(`/${locIdentifier}/services`);
    } else if (currentPath.includes('/request-offer')) {
      navigate(`/${locIdentifier}/request-offer`);
    } else if (currentPath.includes('/transportation')) {
      navigate(`/${locIdentifier}/transportation`);
    } else {
      navigate(`/${locIdentifier}/${section}`);
    }
  };

  if (isLoading) {
    return (
      <span className={cn('text-base font-semibold text-content-text-muted truncate', className)}>
        Loading...
      </span>
    );
  }

  if (!typedLocations.length) {
    return (
      <span className={cn('text-base font-semibold text-content-text-muted truncate', className)}>
        No locations
      </span>
    );
  }

  const label = selectedLocation?.name || 'Select location';
  const canSwitch = selectableLocations.length > 0;

  if (!canSwitch) {
    return (
      <span
        className={cn('truncate text-base font-semibold text-content-text', className)}
        title={label}
      >
        {label}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex max-w-full min-w-0 items-center gap-1 rounded px-0 py-0.5 text-left',
          'text-base font-semibold text-content-text outline-none',
          'hover:text-content-text-muted focus:outline-none data-[state=open]:text-content-text-muted',
          className
        )}
        aria-label={`Current location: ${label}. Open location menu.`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-content-text-muted" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="z-[200] min-w-[12rem] max-h-80 overflow-y-auto p-1"
        collisionPadding={12}
      >
        {selectableLocations.map((location) => (
          <DropdownMenuItem
            key={location.id}
            className="cursor-pointer text-base"
            onSelect={() => handleLocationSelect(location)}
          >
            <span className="truncate">{location.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
